import React, { useEffect, useState } from 'react';
import { BedDouble, ImagePlus, Plus, X } from 'lucide-react';
import { api, resolveUrl, type LodgingBooking, type LodgingExtraUnit, type LodgingPriceUnit, type MarkLodging, type MarkLodgingInput } from '../api';
import { TYPE, useTheme } from '../theme';
import { Badge, Button, Card, CardHeader, Field, Icon, Input, Segmented, Switch, Textarea } from '../components/ui';
import { kronor } from '../views/jakt/adPurchase';
import { DEFAULT_CANCELLATION, capacityText, extraText, guestsText, lodgingPriceText, lodgingStatusText, stayText } from '../views/jakt/lodging';

/**
 * Boende på marken (L1), under Ekonomi: pris per natt, bäddar, tillval och avbokningsregler,
 * samt bokningarna. Bokning och betalning går via markens Stripe-konto (samma som annonsköp).
 */
type ExtraMode = 'included' | 'extra' | 'none';
interface Form {
  id: string | null; name: string; description: string; pricePerNight: string; priceUnit: LodgingPriceUnit; capacity: string; rooms: string;
  linens: ExtraMode; linensPrice: string; linensUnit: LodgingExtraUnit;
  cleaning: ExtraMode; cleaningPrice: string; cleaningUnit: LodgingExtraUnit;
  checkInTime: string; checkOutTime: string; cancellationPolicy: string; active: boolean;
  images: string[]; checkInInfo: string; checkOutInfo: string; cleaningInfo: string;
}

/** EK2: bilder sparas som /uploads/…-sökväg så att de fungerar oavsett API-adress. */
export const uploadPath = (url: string) => { const i = url.indexOf('/uploads/'); return i >= 0 ? url.slice(i) : url; };

const modeOf = (included: boolean, price: number | null): ExtraMode => (included ? 'included' : price != null && price > 0 ? 'extra' : 'none');
const str = (n: number | null | undefined) => (n == null ? '' : String(n));

function toForm(l: MarkLodging | null): Form {
  return {
    id: l?.id ?? null, name: l?.name ?? '', description: l?.description ?? '', pricePerNight: str(l?.pricePerNight), priceUnit: l?.priceUnit ?? 'lodging',
    capacity: str(l?.capacity ?? 4), rooms: str(l?.rooms),
    linens: l ? modeOf(l.linensIncluded, l.linensPrice) : 'none', linensPrice: str(l?.linensPrice), linensUnit: l?.linensUnit ?? 'person',
    cleaning: l ? modeOf(l.cleaningIncluded, l.cleaningPrice) : 'none', cleaningPrice: str(l?.cleaningPrice), cleaningUnit: l?.cleaningUnit ?? 'booking',
    checkInTime: l?.checkInTime ?? '15:00', checkOutTime: l?.checkOutTime ?? '11:00', cancellationPolicy: l?.cancellationPolicy ?? '', active: l?.active ?? true,
    images: l?.images ?? [], checkInInfo: l?.checkInInfo ?? '', checkOutInfo: l?.checkOutInfo ?? '', cleaningInfo: l?.cleaningInfo ?? '',
  };
}

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

function toInput(markId: string, f: Form): MarkLodgingInput {
  return {
    id: f.id, markId, name: f.name, description: f.description || null,
    pricePerNight: num(f.pricePerNight) ?? 0, priceUnit: f.priceUnit, capacity: Number(f.capacity) || 0, rooms: num(f.rooms),
    linensIncluded: f.linens === 'included', linensPrice: f.linens === 'extra' ? num(f.linensPrice) : null, linensUnit: f.linensUnit,
    cleaningIncluded: f.cleaning === 'included', cleaningPrice: f.cleaning === 'extra' ? num(f.cleaningPrice) : null, cleaningUnit: f.cleaningUnit,
    checkInTime: f.checkInTime || null, checkOutTime: f.checkOutTime || null, cancellationPolicy: f.cancellationPolicy || null, active: f.active,
    images: f.images, checkInInfo: f.checkInInfo || null, checkOutInfo: f.checkOutInfo || null, cleaningInfo: f.cleaningInfo || null,
  };
}

export function LodgingSettings({ markId }: { markId: string }) {
  const T = useTheme();
  const [list, setList] = useState<MarkLodging[] | null>(null);
  const [bookings, setBookings] = useState<LodgingBooking[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = () => {
    api.markLodgings(markId, true).then(setList).catch(e => { setErr(e.message); setList([]); });
    api.markLodgingBookings(markId).then(setBookings).catch(() => {});
  };
  useEffect(load, [markId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!form) return;
    setBusy(true); setErr('');
    try {
      const saved = await api.saveMarkLodging(toInput(markId, form));
      setList(l => (l ?? []).some(x => x.id === saved.id) ? (l ?? []).map(x => (x.id === saved.id ? saved : x)) : [...(l ?? []), saved]);
      setForm(null);
    } catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };
  const remove = async (l: MarkLodging) => {
    if (!confirm(`Ta bort ${l.name}? Bokningar som redan gjorts finns kvar.`)) return;
    setBusy(true); setErr('');
    try { await api.deleteMarkLodging(l.id); setList(x => (x ?? []).filter(y => y.id !== l.id)); setForm(null); }
    catch (e) { setErr((e as Error).message); }
    setBusy(false);
  };

  const addImages = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true); setErr('');
    try {
      const urls: string[] = [];
      for (const f of Array.from(files)) urls.push(uploadPath((await api.uploadFile(f)).url));
      setForm(fm => (fm ? { ...fm, images: [...fm.images, ...urls].slice(0, 20) } : fm));
    } catch { setErr('Bilduppladdningen misslyckades'); }
    setUploading(false);
  };

  if (!list) return null;
  const set = (patch: Partial<Form>) => setForm(f => (f ? { ...f, ...patch } : f));
  const upcoming = bookings.filter(b => b.checkOut >= new Date().toISOString().slice(0, 10));

  const extraFields = (label: string, mode: ExtraMode, price: string, unit: LodgingExtraUnit, key: 'linens' | 'cleaning') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{label}</div>
      <Segmented label={label} value={mode} onChange={v => set({ [key]: v } as Partial<Form>)} size="sm"
        options={[{ value: 'included', label: 'Ingår' }, { value: 'extra', label: 'Tillval' }, { value: 'none', label: 'Erbjuds inte' }]} />
      {mode === 'extra' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, alignItems: 'end' }}>
          <Field label="Pris (kr)"><Input inputMode="decimal" value={price} onChange={e => set({ [`${key}Price`]: e.target.value } as Partial<Form>)} data-testid={`lodging-${key}-price`} /></Field>
          <Segmented label={`${label}: enhet`} value={unit} size="sm" onChange={v => set({ [`${key}Unit`]: v } as Partial<Form>)}
            options={[{ value: 'person', label: 'per person' }, { value: 'booking', label: 'per bokning' }]} />
        </div>
      )}
    </div>
  );

  return (
    <Card style={{ marginBottom: 24 }} data-testid="lodging-settings">
      <CardHeader title="Boende" sub="Erbjud boende på marken. Jägare bokar nätter och betalar med kort; pengarna går till markens Stripe-konto."
        action={!form && <Button size="sm" variant="secondary" icon={<Icon as={Plus} size={16} />} onClick={() => setForm(toForm(null))} data-testid="lodging-add">Lägg till</Button>} />

      {!form && list.map((l, i) => (
        <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
          <Icon as={BedDouble} size={20} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{l.name} {!l.active && <Badge>Pausat</Badge>}</div>
            <div style={{ ...TYPE.small, color: T.textMuted }}>
              {[lodgingPriceText(l), capacityText(l), `lakan ${(extraText(l.linensIncluded, l.linensPrice, l.linensUnit) ?? 'erbjuds inte').toLowerCase()}`, `städning ${(extraText(l.cleaningIncluded, l.cleaningPrice, l.cleaningUnit) ?? 'erbjuds inte').toLowerCase()}`].join(' · ')}
            </div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => setForm(toForm(l))}>Ändra</Button>
        </div>
      ))}
      {!form && !list.length && <div style={{ ...TYPE.small, color: T.textMuted }}>Inget boende ännu.</div>}

      {form && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Namn"><Input value={form.name} placeholder="T.ex. Jaktstugan" onChange={e => set({ name: e.target.value })} data-testid="lodging-name" /></Field>
          <Field label="Beskrivning" hint="Standard, avstånd till passen, vad som finns (kök, bastu, el)."><Textarea value={form.description} onChange={e => set({ description: e.target.value })} rows={3} /></Field>
          <Segmented label="Prissättning" value={form.priceUnit} onChange={v => set({ priceUnit: v })}
            options={[{ value: 'lodging', label: 'Hela boendet per natt' }, { value: 'person', label: 'Per person och natt' }]} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Field label={form.priceUnit === 'person' ? 'Pris per person och natt (kr)' : 'Pris per natt (kr)'}><Input inputMode="decimal" value={form.pricePerNight} onChange={e => set({ pricePerNight: e.target.value })} data-testid="lodging-price" /></Field>
            <Field label="Bäddar" hint={form.priceUnit === 'lodging' ? 'Max antal gäster' : 'Delas mellan bokningar'}><Input inputMode="numeric" value={form.capacity} onChange={e => set({ capacity: e.target.value.replace(/\D/g, '') })} data-testid="lodging-capacity" /></Field>
            <Field label="Rum"><Input inputMode="numeric" value={form.rooms} placeholder="Valfritt" onChange={e => set({ rooms: e.target.value.replace(/\D/g, '') })} /></Field>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="lodging-images">
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>Bilder</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8 }}>
              {form.images.map((u, i) => (
                <div key={u} style={{ position: 'relative', aspectRatio: '4 / 3', borderRadius: 8, overflow: 'hidden', border: `1px solid ${T.border}`, background: T.bg }}>
                  <img src={resolveUrl(u)} alt={`Bild ${i + 1} på boendet`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  <button type="button" aria-label={`Ta bort bild ${i + 1}`} onClick={() => set({ images: form.images.filter(x => x !== u) })}
                    style={{ position: 'absolute', top: 4, right: 4, width: 32, height: 32, borderRadius: 16, border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                    <X size={16} />
                  </button>
                </div>
              ))}
              {form.images.length < 20 && (
                <label style={{ aspectRatio: '4 / 3', borderRadius: 8, border: `1px dashed ${T.borderHover}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer', color: T.textSub, fontSize: 13, fontWeight: 600 }}>
                  <ImagePlus size={20} />
                  {uploading ? 'Laddar upp…' : 'Lägg till'}
                  <input type="file" accept="image/*" multiple disabled={uploading} style={{ display: 'none' }} onChange={e => { void addImages(e.target.files); e.target.value = ''; }} data-testid="lodging-image-input" />
                </label>
              )}
            </div>
          </div>
          {extraFields('Lakan och handdukar', form.linens, form.linensPrice, form.linensUnit, 'linens')}
          {extraFields('Städning', form.cleaning, form.cleaningPrice, form.cleaningUnit, 'cleaning')}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <Field label="Incheckning från"><Input type="time" value={form.checkInTime} onChange={e => set({ checkInTime: e.target.value })} /></Field>
            <Field label="Utcheckning senast"><Input type="time" value={form.checkOutTime} onChange={e => set({ checkOutTime: e.target.value })} /></Field>
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 4 }}>Instruktioner till gästerna</div>
          <div style={{ ...TYPE.small, color: T.textMuted, marginTop: -8 }}>Visas för den som bokat och för markens medlemmar, inte i annonsen.</div>
          <Field label="Incheckning" hint="T.ex. var nyckeln finns, kod till dörren, parkering."><Textarea rows={3} value={form.checkInInfo} onChange={e => set({ checkInInfo: e.target.value })} data-testid="lodging-checkin-info" /></Field>
          <Field label="Utcheckning" hint="T.ex. lämna nyckeln, stäng av värmen, sopor."><Textarea rows={3} value={form.checkOutInfo} onChange={e => set({ checkOutInfo: e.target.value })} data-testid="lodging-checkout-info" /></Field>
          <Field label="Städning" hint="Vad gästen ska städa själv, var städsakerna finns."><Textarea rows={3} value={form.cleaningInfo} onChange={e => set({ cleaningInfo: e.target.value })} data-testid="lodging-cleaning-info" /></Field>
          <Field label="Avbokningsregler" hint="Visas för den som bokar innan betalningen. Avbokning och återbetalning sköts av dig direkt med gästen tills vidare.">
            <Textarea value={form.cancellationPolicy} rows={3} placeholder={`T.ex. "Kostnadsfri avbokning fram till 14 dagar före ankomst, därefter ingen återbetalning." Lämnas tomt: ${DEFAULT_CANCELLATION}`} onChange={e => set({ cancellationPolicy: e.target.value })} />
          </Field>
          <Switch checked={form.active} onChange={v => set({ active: v })} label="Går att boka" labelPosition="right" />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button loading={busy} disabled={uploading} onClick={save} data-testid="lodging-save">Spara boende</Button>
            <Button variant="ghost" disabled={busy} onClick={() => { setForm(null); setErr(''); }}>Avbryt</Button>
            {form.id && <Button variant="danger" disabled={busy} onClick={() => { const l = list.find(x => x.id === form.id); if (l) void remove(l); }}>Ta bort</Button>}
          </div>
        </div>
      )}
      {err && <div role="alert" style={{ color: T.danger, fontSize: 13, fontWeight: 600, marginTop: 10 }}>{err}</div>}

      {!form && list.length > 0 && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}` }} data-testid="lodging-bookings">
          <div style={{ ...TYPE.label, color: T.textMuted, marginBottom: 4 }}>Kommande bokningar ({upcoming.length})</div>
          {upcoming.map(b => {
            const s = lodgingStatusText(b.status);
            return (
              <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{b.userName || b.userEmail} · {b.lodgingName}</div>
                  <div style={{ ...TYPE.small, color: T.textMuted }}>{stayText(b.checkIn, b.checkOut)} · {guestsText(b.guests)} · {kronor(b.amount)}{b.adTitle ? ` · med ${b.adTitle}` : ''}</div>
                </div>
                <Badge tone={s.tone === 'ok' ? 'ok' : s.tone === 'wait' ? 'accent' : 'neutral'}>{s.label}</Badge>
              </div>
            );
          })}
          {!upcoming.length && <div style={{ ...TYPE.small, color: T.textMuted }}>Inga bokningar.</div>}
        </div>
      )}
    </Card>
  );
}
